import type {
  ActionStatusType,
  SourceStatusType,
  CheckoutDataType,
  PaymentMethodEnum,
  PaymentMethodType,
  TerminalStateType,
  AllPaymentProvidersEnum,
} from '@am3nd-org/pointcraft-types';

import { paths } from 'src/routes/paths';

import type {
  FailedLocalPayment,
  LocalTerminalPayment,
  CompletedLocalPayment,
} from 'src/utils/Services/local-terminal';

/**
 * Payment flow states - represents the current state of the payment process
 *
 * Firebase-First Flow (source of truth is Firebase Realtime Database):
 * IDLE → VALIDATING → PROCESSING → AWAITING_CARD → TERMINAL_APPROVED → SUCCESS
 *                          ↓                              ↓
 *                    (Firebase monitoring         (Optimistic success UI)
 *                     starts here via                     ↓
 *                     checkoutId from API)    (Firebase confirms isFullyPaid)
 *
 * Key principle: Terminal state is optimistic, Firebase isFullyPaid is authoritative.
 */
export enum PaymentFlowState {
  /** Initial state - user selecting payment method */
  IDLE = 'IDLE',
  /** Validating checkout totals before processing */
  VALIDATING = 'VALIDATING',
  /** Payment being processed (API called, Firebase monitoring enabled) */
  PROCESSING = 'PROCESSING',
  /** Waiting for customer to tap/insert/swipe card */
  AWAITING_CARD = 'AWAITING_CARD',
  /** Terminal approved - showing optimistic success, awaiting Firebase confirmation */
  TERMINAL_APPROVED = 'TERMINAL_APPROVED',
  /** @deprecated Use TERMINAL_APPROVED instead. Kept for backward compatibility. */
  SYNCING = 'TERMINAL_APPROVED',
  /** Payment completed - Firebase confirmed isFullyPaid */
  SUCCESS = 'SUCCESS',
  /** Payment failed - can retry */
  FAILED = 'FAILED',
  /** Payment cancelled by user */
  CANCELLED = 'CANCELLED',
}

/**
 * Error information for failed payments
 */
export interface PaymentError {
  code: string;
  message: string;
  originalError?: unknown;
}

/**
 * One submission attempt against a payment leg. The `transactionId` matches
 * the `FinancialPaymentTransactionType.id` stored on the checkout so attempts
 * can be cross-referenced with the ledger. A retry appends a new attempt to
 * the same leg — history is never dropped.
 */
export interface LegAttempt {
  transactionId: string;
  status: ActionStatusType;
  startedAt: Date;
}

/**
 * A normalized leg the engine executes. Single payment is a session with
 * `legs.length === 1`. Split payments will reintroduce multi-leg sessions
 * in phase 2; until then `legsRef` always holds exactly one leg.
 */
export interface PaymentLeg {
  legId: string;
  method: PaymentMethodType;
  amount: number;
  attempts: LegAttempt[];
}

/**
 * Lifecycle of a split-payment session. The session is owned by
 * `useV4PaymentFlow` and is the single source of truth for the split UI.
 *
 *  editing   — cashier is configuring legs in the right-panel editor.
 *              Card amounts can change; re-seeding from a fresh `balanceDue`
 *              (e.g. Remove Fees toggle) is allowed. No leg has attempts.
 *  active    — first `startLeg` call has flipped the session live.
 *              `originalBalanceDue` is frozen, leg amounts and methods are
 *              locked, validation switches to active mode (no comparison
 *              against live `balanceDue`).
 *  complete  — Firebase confirmed `isFullyPaid` after the final leg.
 *  failed    — terminal failure on the single-leg path. Split mode keeps
 *              `status='active'` after a leg failure (per-card Retry path).
 */
export type SplitSessionStatus = 'editing' | 'active' | 'complete' | 'failed';

/**
 * Stable container for a split-payment session. Replaces the free-standing
 * `legs` + `activeLegId` pair for split mode by carrying the original
 * (frozen) balance and lifecycle status alongside the legs.
 *
 * Single-payment mode does NOT use this — the hook keeps using `legs` /
 * `activeLegId` directly for one-leg sessions so existing single-payment
 * behavior is unchanged.
 */
export interface SplitPaymentSession {
  sessionId: string;
  status: SplitSessionStatus;
  /**
   * Captured at session creation. Stays constant across the session — used
   * for SplitPanel header math, editor display while active, and any
   * defensive headroom checks. Never overwritten from `currentCheckout`.
   */
  originalBalanceDue: number;
  /**
   * Mirror of `currentCheckout.financialInfo.balanceDue`, kept for read-only
   * display surfaces (e.g. debug panel). Never used for split UI math after
   * `status === 'active'`.
   */
  liveBalanceDue: number;
  activeLegId: string | null;
  legs: PaymentLeg[];
  /** Set when status flips to active. Editor controls become no-ops after this. */
  legsLockedAt: Date | null;
}

/**
 * How an `executeLeg` call left the flow. The caller uses this to decide
 * whether to surface a retry UI, start the next leg, or simply wait for
 * Firebase to confirm.
 */
export type LegExecutionResolution =
  /**
   * Backend accepted the request — leg attempt is PENDING and waits for
   * the Firebase RealtimeDB payment status to flip it SUCCESS / FAILED.
   * Used by every method (CARD, CASH, DEBT, EBT, manual card).
   */
  | 'awaiting_firebase'
  /** API rejected, platform mismatch, or unexpected throw — retryable at the leg level. */
  | 'leg_failed'
  /** Validation blocked submission; no PENDING tx written. */
  | 'rejected_pre_submit';

export interface LegExecutionOutcome {
  /** Updated full leg list with the new attempt appended (caller mirrors into state). */
  legs: PaymentLeg[];
  resolution: LegExecutionResolution;
  error?: PaymentError | null;
}

/**
 * Snapshot the validator and engine need. Always built from a freshly-fetched
 * checkout so we never race the store.
 */
export interface LegContext {
  checkout: CheckoutDataType;
  /** All other legs in the current session (excludes the leg under test). */
  priorLegs: PaymentLeg[];
  customerId: string | null;
  isNativePlatform: boolean;
  currentTerminalProvider: AllPaymentProvidersEnum | null | undefined;
  inPersonPaymentProvider: AllPaymentProvidersEnum | null | undefined;
}

/**
 * Per-card UI state for split payments. Derived from a leg's last attempt
 * status plus session context (which leg is the next one allowed to start,
 * whether any leg is currently in flight). Used by SplitCard to pick its
 * variant without leaking flow-state plumbing into the UI.
 */
export type SplitLegUiStatus =
  /** Last attempt SUCCESS — leg is paid, locked. */
  | 'paid'
  /** Last attempt PENDING — this is the active leg. */
  | 'processing'
  /** Last attempt FAILED — per-card Retry button visible. */
  | 'failed'
  /** No attempts yet AND this is the next payable leg AND no other leg is in flight. */
  | 'next-ready'
  /** Editor row, or earlier leg unresolved — no actions available. */
  | 'waiting';

/**
 * Structured validator output. Caller decides whether to surface (snackbar,
 * inline chip, disabled button). Engine rejects pre-submit when `ok === false`.
 */
export interface LegValidationIssue {
  code: string;
  message: string;
}

export interface LegValidation {
  ok: boolean;
  errors: LegValidationIssue[];
  warnings: LegValidationIssue[];
  derived: {
    balanceDue: number;
    /**
     * Sum of SUCCESS attempts across priorLegs. Already reflected in
     * `balanceDue` via the checkout store, so NOT subtracted from `headroom`
     * — exposed for telemetry and as the gate that skips structural
     * `verifyCheckoutTotals` checks once any leg has paid.
     */
    priorSuccessTotal: number;
    /** Sum of PENDING attempts across priorLegs (inflight; reserved). */
    priorInflightTotal: number;
    /** balanceDue - priorInflightTotal. (SUCCESS is already excluded from balanceDue.) */
    headroom: number;
    requiresLocalTerminal: boolean;
  };
}

/**
 * Payment context - holds all state for the payment flow
 */
export interface PaymentFlowContext {
  /** Current state of the payment flow */
  state: PaymentFlowState;
  /** Error details if payment failed */
  error: PaymentError | null;
  /** Checkout ID once created */
  checkoutId: string | null;
  /** Number of retry attempts */
  retryCount: number;
  /** Selected payment method */
  paymentMethod: PaymentMethodType | null;
  /** Payment amount */
  amount: number;
  /** Client secret for manual card payments */
  clientSecret: string | null;
  /** Payment intent ID for manual card payments */
  paymentIntentId: string | null;
  /** Whether terminal has approved (for optimistic UI during TERMINAL_APPROVED state) */
  terminalApproved: boolean;
  /** Timestamp when terminal approved (for timeout tracking) */
  terminalApprovedAt: Date | null;
}

/**
 * Return type for the useV4PaymentFlow hook
 */
export interface UseV4PaymentFlowReturn {
  // State
  state: PaymentFlowState;
  error: PaymentError | null;
  isProcessing: boolean;
  isSuccess: boolean;
  isFailed: boolean;
  isAwaitingCard: boolean;
  /** Terminal has approved payment, awaiting Firebase confirmation */
  isTerminalApproved: boolean;
  /** @deprecated Use isTerminalApproved instead. Kept for backward compatibility. */
  isSyncing: boolean;

  // Derived from useCloudCheckoutStatus
  /** Tip amount captured by the payment terminal, in dollars. 0 when no tip. */
  tippedAmount: number;
  checkoutStatus: SourceStatusType;
  terminalState: TerminalStateType;
  isFullyPaid: boolean;
  /** Whether Firebase Realtime Database is connected */
  isFirebaseConnected: boolean;
  /** Manually recheck the checkout status from Firebase (one-time read) */
  recheckStatus: () => Promise<void>;
  /** Whether a Firebase status recheck is in progress */
  isRechecking: boolean;
  /** Manually recheck payment status via backend API (queries payment provider directly) */
  recheckBackendStatus: () => Promise<unknown>;
  /** Whether a backend payment status recheck is in progress */
  isRecheckingBackend: boolean;
  /** Check sale status via lightweight endpoint; applies paid result if confirmed */
  handleManualStatusCheck: () => Promise<void>;
  /** Whether a lightweight manual status check is in progress */
  isCheckingManualStatus: boolean;
  /** Fallback polling diagnostics */
  fallbackPolling: { isPolling: boolean; attemptCount: number; expired: boolean };

  // Payment context
  paymentMethod: PaymentMethodType | null;
  amount: number;
  retryCount: number;

  // Local terminal payment flow state
  localTerminalProgress: number;
  localTerminalCurrentPayment: LocalTerminalPayment | null;
  localTerminalCompletedPayments: CompletedLocalPayment[];
  localTerminalFailedPayments: FailedLocalPayment[];
  isLocalTerminalFlow: boolean;

  // Sync timeout modal state (for local terminal flow)
  showSyncTimeoutModal: boolean;
  onAcknowledgeSyncTimeout: () => void;
  /** Countdown seconds until sync timeout modal appears (null when not counting) */
  syncCountdownSeconds: number | null;

  // Split-mode surface
  /** All legs in the current session. Single payment is a one-element array. */
  legs: PaymentLeg[];
  /** Id of the leg currently in flight, or null when nothing is processing. */
  activeLegId: string | null;
  /** True once the session has more than one leg (split mode). */
  isSplitMode: boolean;
  /**
   * The owned split-payment session. Non-null whenever the cashier is in
   * SPLIT mode (status='editing' before Pay, 'active' once a leg starts,
   * 'complete' after Firebase confirms isFullyPaid). Cleared by `reset()`
   * and by `clearSplitSession()` when the cashier toggles away from SPLIT
   * before any leg has attempts.
   */
  splitSession: SplitPaymentSession | null;

  // Actions
  startPayment: (method: PaymentMethodType, amount: number) => Promise<void>;
  /**
   * Build a fresh editing session from the current `balanceDue`. No-op if
   * `balanceDue <= 0` (logged via `SPLIT_BALANCE_NEGATIVE_DETECTED`).
   */
  createSplitSession: (balanceDue: number) => void;
  /**
   * Editor mutators. Each one is gated to `status==='editing'` — calls in
   * any other state are silently ignored (Defense in depth; UI also gates).
   */
  editSplit: SplitEditActions;
  /** Discard the current editing session. Used when the cashier leaves SPLIT before starting. */
  clearSplitSession: () => void;
  /**
   * Start a specific leg by id. Driven by per-card Pay Now buttons. For the
   * first call against an editing session, also activates the session
   * (freezes legs, flips status to 'active'). For subsequent calls, enforces
   * sequential gating — every earlier leg must have resolved SUCCESS.
   */
  startLeg: (legId: string) => Promise<void>;
  /** Retry a failed leg in place. Appends a new PENDING attempt to the same legId. */
  retryLeg: (legId: string) => Promise<void>;
  retry: () => Promise<void>;
  cancel: () => Promise<void>;
  reset: () => void;
}

/**
 * Editor-side mutators exposed by the hook. Thin wrappers around the
 * split-edit helpers; each one is gated to `status==='editing'`.
 */
export interface SplitEditActions {
  addLeg: () => void;
  removeLeg: (legId: string) => void;
  setMethod: (legId: string, method: PaymentMethodEnum) => void;
  setAmount: (legId: string, amount: number) => void;
  /** Redistribute balanceDue evenly across all legs, preserving methods. */
  autoSplit: () => void;
}

/**
 * V4 Payment method types - UI-level payment options
 */
export type V4PaymentMethod = 'CARD' | 'CASH' | 'SPLIT' | 'BNPL' | 'EBT';

/**
 * Props for the PaymentProcessingView component
 */
export interface PaymentProcessingViewProps {
  state: PaymentFlowState;
  terminalState: TerminalStateType;
  error: PaymentError | null;
  amount: number;
  onRetry: () => void;
  onCancel: () => void;
  /** Handler to navigate back to sale (shown in FAILED/CANCELLED states) */
  onBackToSale?: () => void;
  /** Override label on the Back to Sale button */
  backButtonLabel?: string;
  /** Whether terminal has approved and we're awaiting Firebase confirmation */
  isTerminalApproved?: boolean;
  /** @deprecated Use isTerminalApproved instead */
  isSyncing?: boolean;
  /** Countdown seconds until sync timeout modal appears */
  syncCountdownSeconds?: number | null;
  /** Whether to show the countdown timer for local terminal payments */
  showTimer?: boolean;
  /** Duration of the countdown timer in seconds (default: 30) */
  timerDurationSeconds?: number;
  /** Current retry count - used to reset timer on retry */
  retryCount?: number;
  /** Whether to show the dev-only virtual trigger button (dev env + Stripe provider). */
  showVirtualTrigger?: boolean;
  /** Async handler to fire the virtual payment API call. */
  onTriggerVirtualPayment?: () => Promise<void>;
  /** Handler for the orange "Cancel Sale" button shown during AWAITING_CARD and PROCESSING. */
  onCancelSale?: () => void;
}

/**
 * Props for the PaymentSuccessView component
 */
export interface PaymentSuccessViewProps {
  amount: number;
  paymentMethod: PaymentMethodType | null;
  backgroundImage?: string;
  /** Render the success view as a split-payment summary ("Paid via Split"). */
  isSplit?: boolean;
  /**
   * Per-leg breakdown rendered below the "Paid via Split" badge. Required
   * for `isSplit && legs.length >= 2`; ignored for single-payment.
   */
  legs?: PaymentLeg[];
  /** Tip amount captured by the terminal, in dollars. Triggers celebration when > 0. */
  tippedAmount?: number;
}

/**
 * Props for the PaymentSuccessOptions component (right panel)
 */
export interface PaymentSuccessOptionsProps {
  paymentMethod: PaymentMethodType | null;
  /** Render the success header as a split-payment summary ("Paid via Split"). */
  isSplit?: boolean;
  onCloseSale: () => void;
  onPrintReceipt: () => void;
  onSendEmail: (email: string) => void;
  onSendText: (phone: string) => void;

  // Loading states
  isPrinting?: boolean;
  isSendingEmail?: boolean;
  isSendingSms?: boolean;

  // Success states
  emailSent?: boolean;
  smsSent?: boolean;

  // Printer status
  isPrinterReady?: boolean;

  // Reset callbacks (for "Send Another" flow)
  onResetEmailSent?: () => void;
  onResetSmsSent?: () => void;

  // Order management — shown when the business has canProcessOrders enabled
  canProcessOrders?: boolean;
  /** Order number if one was already created before payment; null means no order yet. */
  existingOrderNumber?: string | null;
  onCreateOrder?: () => void;

  // Print Order — only shown for Restaurant/original POS with canProcessOrders + an existing order
  /** When true, the yellow Print Order button is visible. */
  showPrintOrder?: boolean;
  /** Callback for the Print Order button. */
  onPrintOrder?: () => void;
  /** Loading state while the order is being printed. */
  isPrintingOrder?: boolean;

  /** When provided and printer is unavailable, a yellow (?) button appears to launch troubleshooting. */
  onTroubleshootPrinter?: () => void;
}

/**
 * Valid return destinations after payment completion.
 * Used with `navigateBackTo` query param to track where user came from.
 */
export enum PaymentReturnDestination {
  POS = 'pos',
  POS_V2 = 'pos-v2',
  QUICK_PAY = 'quick-pay',
  V3_POS = 'v3-pos',
  RESTAURANT_POS = 'restaurant-pos',
}

/**
 * Maps a PaymentReturnDestination enum value to its corresponding route path.
 * Returns the POS path as default fallback if destination is invalid.
 */
export const getReturnPath = (destination: PaymentReturnDestination | null): string => {
  if (!destination) {
    return paths.dashboard.pos;
  }

  const pathMap: Record<PaymentReturnDestination, string> = {
    [PaymentReturnDestination.POS]: paths.dashboard.pos,
    [PaymentReturnDestination.POS_V2]: paths.dashboard.posV2,
    [PaymentReturnDestination.QUICK_PAY]: paths.dashboard.quickPay,
    [PaymentReturnDestination.V3_POS]: paths.dashboard.v3.pos,
    [PaymentReturnDestination.RESTAURANT_POS]: paths.dashboard.v4.restaurantPos,
  };

  return pathMap[destination] || paths.dashboard.pos;
};

/**
 * Human-readable "Back to …" label for the originating app, derived from the
 * navigateBackTo query param. Used by the cart preview's return button so the
 * cashier sees exactly which app they'll return to.
 */
export const getReturnDestinationLabel = (
  destination: PaymentReturnDestination | null
): string => {
  switch (destination) {
    case PaymentReturnDestination.RESTAURANT_POS:
      return 'Back to Restaurant POS';
    case PaymentReturnDestination.V3_POS:
      return 'Back to Point of Sale V3';
    case PaymentReturnDestination.QUICK_PAY:
      return 'Back to Quick Pay';
    case PaymentReturnDestination.POS:
    case PaymentReturnDestination.POS_V2:
      return 'Back to Point of Sale';
    default:
      return 'Back to Sale';
  }
};

/**
 * Payment sound configuration
 * Defines how many times each sound should play on payment events
 */
export const PAYMENT_SOUND_CONFIG = {
  /** Number of times success sound plays */
  SUCCESS_PLAY_COUNT: 1,
  /** Number of times failure sound plays */
  FAILURE_PLAY_COUNT: 3,
  /** Volume level (0.0 to 1.0) */
  VOLUME: 0.7,
  /** Interval between repeated plays in milliseconds */
  INTERVAL_MS: 2000,
} as const;
